import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Wine, Plus, BarChart3, User, LogOut, Settings, Menu, X, MapPin } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AppIcon } from '@/components/icons/AppIcon';
import { LocationManagementModal } from '@/components/wine/LocationManagementModal';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showLocationModal, setShowLocationModal] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle scroll to show/hide header
  React.useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // Scrolling down - hide header
        setIsHeaderVisible(false);
        setMobileMenuOpen(false); // Close mobile menu when hiding header
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  // Nav items per desktop (tutte le voci)
  const desktopNavItems = [
    { to: '/', icon: Wine, label: 'Cantina' },
    { to: '/add', icon: Plus, label: 'Aggiungi' },
    { to: '/stats', icon: BarChart3, label: 'Statistiche' },
    { to: '/profile', icon: User, label: 'Profilo' },
    { to: null, icon: MapPin, label: 'Posizioni', onClick: () => setShowLocationModal(true) },
  ];

  // Nav items per bottom navigation mobile (solo 3 voci)
  const mobileBottomNavItems = [
    { to: '/', icon: Wine, label: 'Cantina' },
    { to: '/add', icon: Plus, label: 'Aggiungi' },
    { to: '/stats', icon: BarChart3, label: 'Statistiche' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-wine-white">
      {/* Header Desktop (>= 1024px) */}
      <header className={`hidden lg:block bg-[#95102d] shadow-wine sticky top-0 z-40 transition-transform duration-300 ${!isHeaderVisible ? '-translate-y-full' : 'translate-y-0'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <AppIcon className="w-10 h-10" />
              <div>
                <h1 className="text-xl font-serif font-bold text-[rgb(255,239,226)] tracking-wide">MyWine</h1>
                <p className="text-xs text-wine-cork">La Tua Cantina Digitale</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex items-center gap-4">
              {desktopNavItems.map((item) =>
                item.to ? (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                        isActive
                          ? 'bg-wine-parchment text-wine-burgundy shadow-gold'
                          : 'text-wine-parchment hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ) : (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-wine-parchment hover:bg-white/10 hover:text-white transition-all"
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                )
              )}
            </nav>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {isAdmin && (
                <NavLink
                  to="/admin"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-wine-gold hover:bg-white/10 transition-all"
                >
                  <Settings className="w-5 h-5" />
                  <span>Admin</span>
                </NavLink>
              )}

              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-wine-parchment hover:bg-red-900/30 hover:text-white transition-all"
              >
                <LogOut className="w-5 h-5" />
                <span>Esci</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Header Mobile (< 1024px) - Top Bar with Hamburger */}
      <header className={`lg:hidden bg-[#95102d] shadow-wine sticky top-0 z-40 transition-transform duration-300 ${!isHeaderVisible ? '-translate-y-full' : 'translate-y-0'}`}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <AppIcon className="w-8 h-8" />
              <div>
                <h1 className="text-lg font-serif font-bold text-[rgb(255,239,226)] tracking-wide">MyWine</h1>
              </div>
            </div>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-all"
              aria-label="Menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[rgb(255,239,226)]" />
              ) : (
                <Menu className="w-6 h-6 text-[rgb(255,239,226)]" />
              )}
            </button>
          </div>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="py-3 space-y-1 border-t border-wine-cork/30 mt-3">
                  {isAdmin && (
                    <NavLink
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-wine-gold hover:bg-white/10 transition-all font-medium"
                    >
                      <Settings className="w-5 h-5" />
                      <span>Admin</span>
                    </NavLink>
                  )}

                  <NavLink
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg text-wine-parchment hover:bg-white/10 transition-all font-medium"
                  >
                    <User className="w-5 h-5" />
                    <span>Profilo</span>
                  </NavLink>

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowLocationModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-wine-parchment hover:bg-white/10 transition-all font-medium"
                  >
                    <MapPin className="w-5 h-5" />
                    <span>Posizioni</span>
                  </button>

                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleSignOut();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white hover:bg-red-900/30 transition-all font-medium"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Esci</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8 pb-28 md:pb-32 lg:pb-8">
        {children}
      </main>

      {/* Bottom Navigation Mobile (< 1024px) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#95102d] shadow-wine z-50">
        <div className="relative flex items-center justify-around px-2 py-3">
          {mobileBottomNavItems.map((item, index) => {
            // Pulsante centrale (Aggiungi) - rotondo e rialzato
            if (index === 1) {
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="absolute left-1/2 -translate-x-1/2 -top-4"
                >
                  {({ isActive }) => (
                    <motion.div
                      animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                      transition={{ duration: 0.2 }}
                      className={`w-14 h-14 rounded-full shadow-gold flex items-center justify-center border-[3px] border-solid border-[rgb(223,211,195)] ${
                        isActive
                          ? 'bg-wine-parchment text-wine-burgundy'
                          : 'bg-wine-parchment text-wine-burgundy'
                      }`}
                    >
                      <item.icon className="w-7 h-7" />
                    </motion.div>
                  )}
                </NavLink>
              );
            }

            // Pulsanti laterali (Cantina e Statistiche)
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="flex flex-col items-center gap-1 px-4 py-2 rounded-lg transition-all text-[rgb(255,239,226)]"
              >
                {({ isActive }) => (
                  <>
                    <motion.div
                      animate={isActive ? { scale: 1.1 } : { scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <item.icon className="w-6 h-6" />
                    </motion.div>
                    <span className={`text-xs font-medium ${isActive ? 'underline underline-offset-2' : ''}`}>
                      {item.label}
                    </span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Location Management Modal */}
      <LocationManagementModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
      />
    </div>
  );
};
